import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/common/Button';
import { supabase } from '../lib/supabase';
import type { CreditGiftConfig, CreditGiftMode } from '../types/credits';
import { toast } from 'react-toastify';

const DEFAULT_CONFIG: CreditGiftConfig = {
  id: 1,
  mode: null,
  signup_credits_freelancer: 0,
  signup_credits_recruiter: 0,
  monthly_credits_freelancer: 0,
  monthly_credits_recruiter: 0,
  monthly_credits_obnl: 0,
  monthly_cumulative: false,
  updated_at: '',
};

export default function CreditGiftConfigPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [config, setConfig] = useState<CreditGiftConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from('credit_gift_config')
        .select('*')
        .eq('id', 1)
        .single();
      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }
      setConfig({
        ...DEFAULT_CONFIG,
        ...data,
        mode: (data?.mode as CreditGiftMode) ?? null,
      });
      setLoading(false);
    };
    load();
  }, []);

  const handleModeChange = (mode: CreditGiftMode) => {
    setConfig(prev => ({ ...prev, mode }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      config.mode === 'signup' &&
      (config.signup_credits_freelancer < 0 || config.signup_credits_recruiter < 0)
    ) {
      toast.error(
        t('creditGiftConfig.invalidAmounts', {
          ns: 'admin',
          defaultValue: 'Amounts must be 0 or more',
        })
      );
      return;
    }
    if (
      config.mode === 'monthly' &&
      (config.monthly_credits_freelancer < 0 ||
        config.monthly_credits_recruiter < 0 ||
        config.monthly_credits_obnl < 0)
    ) {
      toast.error(
        t('creditGiftConfig.invalidAmounts', {
          ns: 'admin',
          defaultValue: 'Amounts must be 0 or more',
        })
      );
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('credit_gift_config')
        .update({
          mode: config.mode,
          signup_credits_freelancer: config.signup_credits_freelancer,
          signup_credits_recruiter: config.signup_credits_recruiter,
          monthly_credits_freelancer: config.monthly_credits_freelancer,
          monthly_credits_recruiter: config.monthly_credits_recruiter,
          monthly_credits_obnl: config.monthly_credits_obnl,
          monthly_cumulative: config.monthly_cumulative,
        })
        .eq('id', 1);
      if (error) throw error;
      toast.success(
        t('creditGiftConfig.saved', { ns: 'admin', defaultValue: 'Configuration saved' })
      );
    } catch (err) {
      toast.error(
        (err as Error).message ||
          t('errors.generic', { ns: 'common', defaultValue: 'An error occurred' })
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <p className="text-gray-600">
          {t('loading', { ns: 'common', defaultValue: 'Loading...' })}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
      <h1 className="text-5xl font-bold hyphens-auto">
        {t('creditGiftConfig.title', { ns: 'admin', defaultValue: 'Credit gifts' })}
      </h1>
      <p className="mt-2 text-gray-600">
        {t('creditGiftConfig.subtitle', {
          ns: 'admin',
          defaultValue:
            'Choose one option: signup credits or monthly credits. They cannot be active at the same time.',
        })}
      </p>

      <form onSubmit={handleSave} className="mt-6 space-y-6 max-w-xl">
        <div>
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-gray-900">
              {t('creditGiftConfig.modeLabel', {
                ns: 'admin',
                defaultValue: 'Automatic credit gift',
              })}
            </legend>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={config.mode === null}
                onChange={() => handleModeChange(null)}
                className="text-primary focus:ring-primary"
              />
              <span>{t('creditGiftConfig.modeOff', { ns: 'admin', defaultValue: 'Off' })}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={config.mode === 'signup'}
                onChange={() => handleModeChange('signup')}
                className="text-primary focus:ring-primary"
              />
              <span>
                {t('creditGiftConfig.modeSignup', { ns: 'admin', defaultValue: 'Signup credits' })}
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={config.mode === 'monthly'}
                onChange={() => handleModeChange('monthly')}
                className="text-primary focus:ring-primary"
              />
              <span>
                {t('creditGiftConfig.modeMonthly', {
                  ns: 'admin',
                  defaultValue: 'Monthly credits',
                })}
              </span>
            </label>
          </fieldset>
        </div>

        {config.mode === 'signup' && (
          <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('creditGiftConfig.signupAmounts', {
                ns: 'admin',
                defaultValue: 'Credits on signup (per role)',
              })}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="signup-freelancer"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('role.freelancer', { ns: 'common' })}
                </label>
                <input
                  id="signup-freelancer"
                  type="number"
                  min={0}
                  value={config.signup_credits_freelancer}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      signup_credits_freelancer: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                />
              </div>
              <div>
                <label
                  htmlFor="signup-recruiter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('role.recruiter', { ns: 'common' })}
                </label>
                <input
                  id="signup-recruiter"
                  type="number"
                  min={0}
                  value={config.signup_credits_recruiter}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      signup_credits_recruiter: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                />
              </div>
            </div>
          </div>
        )}

        {config.mode === 'monthly' && (
          <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('creditGiftConfig.monthlyAmounts', {
                ns: 'admin',
                defaultValue: 'Monthly credits (per role)',
              })}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="monthly-freelancer"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('role.freelancer', { ns: 'common' })}
                </label>
                <input
                  id="monthly-freelancer"
                  type="number"
                  min={0}
                  value={config.monthly_credits_freelancer}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      monthly_credits_freelancer: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                />
              </div>
              <div>
                <label
                  htmlFor="monthly-obnl"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('creditGiftConfig.monthlyObnlLabel', {
                    ns: 'admin',
                    defaultValue: 'Organization (OBNL)',
                  })}
                </label>
                <input
                  id="monthly-obnl"
                  type="number"
                  min={0}
                  value={config.monthly_credits_obnl}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      monthly_credits_obnl: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                />
              </div>
              <div>
                <label
                  htmlFor="monthly-recruiter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('role.recruiter', { ns: 'common' })}
                </label>
                <input
                  id="monthly-recruiter"
                  type="number"
                  min={0}
                  value={config.monthly_credits_recruiter}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      monthly_credits_recruiter: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.monthly_cumulative}
                onChange={e =>
                  setConfig(prev => ({ ...prev, monthly_cumulative: e.target.checked }))
                }
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-700">
                {t('creditGiftConfig.monthlyCumulative', {
                  ns: 'admin',
                  defaultValue:
                    'Cumulative (unused credits roll over). If unchecked, balance is reset each month.',
                })}
              </span>
            </label>
          </div>
        )}

        <Button type="submit" variant="primary" disabled={saving}>
          {saving
            ? t('loading', { ns: 'common', defaultValue: 'Loading...' })
            : t('creditGiftConfig.save', { ns: 'admin', defaultValue: 'Save' })}
        </Button>
      </form>
    </div>
  );
}
